import React, {useState} from 'react';
import {useIntl, type IntlShape} from 'react-intl';
import styled from 'styled-components';
import {Button, Detail, EMPHASIS, FlexColumn} from '@forecast-it/design-system';
import {createToast} from '../toasts/toast';
import {
	commitCreateSchedule,
	commitDeleteSchedule,
	commitUpdateSchedule,
	type ScheduleChange,
	type ScheduleSetSnapshot,
	type ScheduleWriteFailure,
} from './scheduleMutations';
import {NEW_SCHEDULE} from './scheduleOptions';
import {browserTimeZone} from './scheduleTimezone';
import {SCHEDULING_MESSAGE} from './schedulingMessages';
import {ScheduleRow} from './ScheduleRow';

const TOAST_DURATION_MS = 5000;

const SectionLabel = styled(Detail)`
	text-transform: uppercase;
	letter-spacing: 0.4px;
`;

// A refusal says the change was not saved; the optimistic update has already rolled back. A conflict or a
// schedule deleted elsewhere also reloads the set, so the drawer shows what is stored rather than what this
// tab last knew.
export const reportRefusal = (
	failure: ScheduleWriteFailure,
	formatMessage: IntlShape['formatMessage'],
	onReload: () => void
): void => {
	if (failure.kind === 'refused') {
		createToast({duration: TOAST_DURATION_MS, message: formatMessage({id: SCHEDULING_MESSAGE.ERROR_REFUSED})});
		return;
	}
	createToast({
		duration: TOAST_DURATION_MS,
		message: formatMessage({
			id: failure.kind === 'conflict' ? SCHEDULING_MESSAGE.ERROR_CONFLICT : SCHEDULING_MESSAGE.ERROR_NOT_FOUND,
		}),
	});
	onReload();
};

type ScheduleListProps = {
	set: ScheduleSetSnapshot;
	onReload: () => void;
};

export const ScheduleList = ({set, onReload}: ScheduleListProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const viewerZone = browserTimeZone();
	// A row is disabled while one of its writes is in flight, so two quick edits cannot land out of order.
	const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
	const mark = (scheduleId: string, isBusy: boolean) =>
		setBusy(current => {
			const next = new Set(current);
			if (isBusy) next.add(scheduleId);
			else next.delete(scheduleId);
			return next;
		});
	const callbacksFor = (scheduleId: string) => ({
		onRefused: (failure: ScheduleWriteFailure) => reportRefusal(failure, formatMessage, onReload),
		onSettled: () => mark(scheduleId, false),
	});

	const update = (scheduleId: string, change: ScheduleChange) => {
		mark(scheduleId, true);
		commitUpdateSchedule(set, scheduleId, change, callbacksFor(scheduleId));
	};
	const remove = (scheduleId: string) => {
		mark(scheduleId, true);
		commitDeleteSchedule(set, scheduleId, callbacksFor(scheduleId));
	};
	// Creates are not retried and not deduplicated, so the button waits for one to settle before the next.
	const [adding, setAdding] = useState(false);
	const add = () => {
		setAdding(true);
		commitCreateSchedule(
			set,
			{...NEW_SCHEDULE, weekdays: [...NEW_SCHEDULE.weekdays], timezone: viewerZone},
			{
				onRefused: (failure: ScheduleWriteFailure) => reportRefusal(failure, formatMessage, onReload),
				onSettled: () => setAdding(false),
			}
		);
	};

	return (
		<FlexColumn gap={'s'} data-cy={'schedule-list'}>
			<SectionLabel color={'auxiliary'}>{formatMessage({id: SCHEDULING_MESSAGE.SECTION})}</SectionLabel>
			{set.schedules.map(schedule => (
				<ScheduleRow
					key={schedule.id}
					schedule={schedule}
					disabled={busy.has(schedule.scheduleId) || schedule.scheduleId.startsWith('client:')}
					viewerZone={viewerZone}
					onChange={change => update(schedule.scheduleId, change)}
					onRemove={() => remove(schedule.scheduleId)}
				/>
			))}
			<div>
				<Button emphasis={EMPHASIS.MEDIUM} onPress={add} disabled={adding} data-cy={'schedule-add'}>
					{formatMessage({id: SCHEDULING_MESSAGE.ADD})}
				</Button>
			</div>
			<Detail color={'auxiliary'}>{formatMessage({id: SCHEDULING_MESSAGE.TIMEZONE_NOTE})}</Detail>
		</FlexColumn>
	);
};
